// Client-side prediction + server reconciliation + catch-up smoothing.
//
// Goal: localhost feel in the steady state — when the server agrees with
// us, the rendered position is EXACTLY the predicted position, no lag,
// no easing, no interpolation. Smoothing only runs to absorb actual
// server disagreement.
//
// Collision: the predicted position is run through a swept-AABB against
// the live wall set (server walls + pending build predictions) on every
// `recordInput` and during every replay step inside `reconcile`. The
// sweep mirrors `server::physics::sweep_aabb` byte-for-byte except for
// the fixed-point → float conversion. Drift from rounding differences
// is sub-cell per tick and gets absorbed by `renderOffset`. Without
// this the predicted self walks straight through walls until the next
// snapshot snaps it back — the classic "rubber-band into cover" feel.
//
// Movement model: velocity-based with separate accel rules for matching
// vs. opposing input. Each input tick:
//
//   1. Build TARGET velocity from input direction × MAX_SPEED. Diagonal
//      input is clamped per-axis to MAX_SPEED × INV_SQRT2 so √2-walking
//      isn't faster than cardinal motion.
//   2. Step the per-axis velocity toward target. If the new target
//      opposes the current velocity on that axis, use MOVE_REVERSE_ACCEL
//      (one tick to zero out a maxed axis — enables counter-strafing).
//      Otherwise use the gentle MOVE_ACCEL.
//   3. Position += velocity (no walls on the client; server's sweep can
//      clamp behind us and the reconcile loop will catch up).
//
// EVERY constant + the step_velocity_axis function MUST stay identical
// to the server's Rust copy in `server::game::systems`. Floating-point
// math here will drift slightly from the server's Q16.16, but small
// drift gets absorbed into `renderOffset` and decays naturally.
//
// Reconciliation rebuilds velocity from history when snapping position
// back to the server's view. We stash the post-input velocity inside
// each `InputSample` we record; on reconcile-to-tick-T, the velocity
// at T+1's starting point is whatever we wrote for tick T.

import { sweepCircle, type WallLookup } from "./physics";

/** One sample of held-input state, plus the velocity it produced. The
 *  velocity fields are written by `recordInput` (and rewritten by
 *  `reconcile`'s replay loop) so we can resume from any acked tick. */
export interface InputSample {
  tick: number;
  moveX: number; // {-1, 0, +1}
  moveY: number;
  /** Velocity AFTER this input was applied. Used by `reconcile` to
   *  restore the velocity state at the acked tick before replaying
   *  later inputs. */
  velocityAfterX: number;
  velocityAfterY: number;
}

export interface Vec2 {
  x: number;
  y: number;
}

// ---------------------------------------------------------------------------
// Tunables — MUST MATCH server::game::systems exactly.
// ---------------------------------------------------------------------------

/** Max walk speed, cells/tick. 0.143 at 30 Hz = 4.29 cells/sec —
 *  35 % off the brief's original 6.6. */
const MAX_SPEED = 0.157; // Q16.16 bits: 10_289

/** Per-tick acceleration when input direction matches (or doesn't
 *  oppose) current velocity. Same ramp SHAPE as before (~8.8 ticks /
 *  ~293 ms rest-to-max), scaled with the rest of the movement budget
 *  so the easing curve feels identical. */
const MOVE_ACCEL = 0.018; // Q16.16 bits: 1_180

/** Per-tick acceleration when input OPPOSES current velocity. At 0.0536,
 *  zeroing a maxed axis takes ~2.7 ticks (~89 ms) — twice as draggy
 *  as the prior 0.165 (counter-strafe nerf), then scaled by 0.65 with
 *  the rest of the movement budget. After zero, MOVE_ACCEL takes
 *  over for the ramp into the new direction. */
const MOVE_REVERSE_ACCEL = 0.059; // Q16.16 bits: 3_867

/** 1/√2 — diagonal target-velocity clamp. */
const INV_SQRT2 = Math.SQRT1_2;

/** Player AABB half-extent. MUST match `server::PLAYER_HALF.x`. The
 *  swept collision below uses this for the inflated-cell slab test. */
const PLAYER_HALF = 0.7;

/** Default wall lookup — no walls anywhere. Used before main.ts wires
 *  the real one (one render frame at most). Prevents the sweep from
 *  thinking the world is full of cells. */
const NO_WALLS: WallLookup = () => false;

/** Exponential time constant for the catch-up offset decay. After this
 *  many ms the offset has decayed to ~37% of its post-reconcile value;
 *  ~3× this and it's invisible. */
const CORRECTION_TIME_CONSTANT_MS = 150;

/** Reconcile deltas larger than this many cells aren't smoothed — they
 *  snap. Covers respawns, deaths, teleports. */
const SNAP_THRESHOLD_CELLS = 2;

/** Reconcile DEADZONE. If the server agrees with our prediction to
 *  within this many cells, we DON'T correct at all — we keep the
 *  locally-integrated predicted position and only retire acked inputs.
 *
 *  Why: the client predicts in floating-point, the server in Q16.16
 *  fixed-point. They disagree by a sub-cell amount EVERY tick. Without
 *  a deadzone, every snapshot (~50 ms) folds that tiny disagreement
 *  into `renderOffset`, which decays over `CORRECTION_TIME_CONSTANT_MS`
 *  — but a fresh poke lands before it settles, so the offset never
 *  reaches zero. The camera follows the rendered self, so that residual
 *  oscillation jitters the ENTIRE world by a fraction of a pixel,
 *  forever. The deadzone lets the prediction free-run inside a sub-cell
 *  tolerance (drift is bounded and self-limiting because real input is
 *  the same on both sides); only genuine disagreement — walls,
 *  knockback, lag spikes — exceeds it and reconciles. 0.12 cells at a
 *  typical ~35 px/cell zoom is ~4 px of slack you can't perceive on a
 *  centered avatar, but it's the difference between buttery and buzzy. */
const RECONCILE_DEADZONE_CELLS = 0.12;

/** Below this magnitude the renderOffset snaps to zero. Math.exp() is
 *  asymptotic; without this we'd render an infinite sub-pixel decay. */
const OFFSET_SNAP_EPSILON = 0.001;

// ---------------------------------------------------------------------------
// Velocity step — mirrors `step_velocity_axis` in server Rust.
// ---------------------------------------------------------------------------

/** Step one axis of velocity toward `target`. Same rule as the server:
 *  REVERSE accel when target opposes current sign, gentle accel
 *  otherwise. Clamps to target if a single step would overshoot. */
function stepVelocityAxis(v: number, target: number): number {
  const diff = target - v;
  if (diff === 0) return v;
  const opposing = (v > 0 && target < 0) || (v < 0 && target > 0);
  const step = opposing ? MOVE_REVERSE_ACCEL : MOVE_ACCEL;
  if (diff > 0) {
    return diff <= step ? target : v + step;
  } else {
    return -diff <= step ? target : v - step;
  }
}

/** Compute target velocity from an input sample. Diagonal inputs are
 *  clamped per-axis by INV_SQRT2 so total magnitude equals MAX_SPEED. */
function targetVelocity(s: { moveX: number; moveY: number }): Vec2 {
  let tx = s.moveX * MAX_SPEED;
  let ty = s.moveY * MAX_SPEED;
  if (s.moveX !== 0 && s.moveY !== 0) {
    tx *= INV_SQRT2;
    ty *= INV_SQRT2;
  }
  return { x: tx, y: ty };
}

// ---------------------------------------------------------------------------
// Engine
// ---------------------------------------------------------------------------

export class PredictionEngine {
  /** Replay queue: every input the client has sent that the server
   *  hasn't yet acknowledged. Drops on reconciliation. Each entry's
   *  `velocityAfter` fields are the state AFTER applying that input
   *  — used by reconcile to restore velocity at the acked tick. */
  private readonly history = new Map<number, InputSample>();

  /** Authoritative predicted position after the latest input. */
  private readonly predicted: Vec2 = { x: 0, y: 0 };

  /** Predicted position at the START of the current input tick — i.e.
   *  the value `predicted` had at the moment of the latest
   *  `recordInput` call, before it was advanced by velocity. The
   *  sub-frame interpolator lerps `predictedPrev → predicted` over
   *  one input interval so motion looks smooth at any refresh rate
   *  instead of stepping in 30 Hz chunks. */
  private readonly predictedPrev: Vec2 = { x: 0, y: 0 };

  /** Current 2D velocity — driven by `stepVelocityAxis` on each input,
   *  mirrors the server's `Velocity` component. Restored from history
   *  on reconcile. */
  private readonly velocity: Vec2 = { x: 0, y: 0 };

  /** Catch-up delta added to the predicted position when rendering.
   *  Zero whenever the server has been agreeing with us. */
  private readonly renderOffset: Vec2 = { x: 0, y: 0 };

  /** `performance.now()` of the last `getRenderPosition` call. */
  private lastRenderTime: number | null = null;

  /** Diagnostic-only — last reconcile bookkeeping for the HUD. */
  private lastReconcileServerPos: Vec2 = { x: 0, y: 0 };
  private lastReconcileInputTick = 0;

  /** Wall lookup callback. Returns true if a cell currently contains
   *  a wall (server walls + pending build predictions). Wired by
   *  main.ts once at startup; the closure captures a Set that main.ts
   *  rebuilds in place each tick + each snapshot. */
  private wallLookup: WallLookup = NO_WALLS;

  /** DIAGNOSTIC: count of reconciles that actually corrected (exceeded
   *  the deadzone) vs were absorbed by it. If `corrections` climbs while
   *  standing still, the server is disagreeing with the mirror beyond
   *  the deadzone — reconcile is a live jitter source. If it stays flat
   *  and `drift` reads ~0 but you still see jitter, the cause is in the
   *  render path, not here. */
  private corrections = 0;
  private deadzoneHits = 0;

  /** Wire the wall lookup. Until called, the predictor behaves as if
   *  the world is empty (zero collision). */
  setWallLookup(lookup: WallLookup): void {
    this.wallLookup = lookup;
  }

  getPredicted(): Readonly<Vec2> {
    return this.predicted;
  }

  /** Current per-axis velocity. Useful for HUD diagnostics — non-zero
   *  while moving, smoothly ramps to zero on key release. */
  getVelocity(): Readonly<Vec2> {
    return this.velocity;
  }

  /** Visual position = lerp(predictedPrev, predicted, factor) + catch-up
   *  offset. The lerp turns 30 Hz simulation updates into smooth motion
   *  at the monitor's refresh rate — without it, a 144 Hz display
   *  shows each predicted position for ~5 frames then jumps ~6 px, and
   *  because the camera follows render position the whole world
   *  twitches with it.
   *
   *  Cost: ~33 ms of pure visual lag (the renderer paints where you
   *  were one input tick ago, not where you are right now). The
   *  authoritative `predicted` is still bang on the latest input — fire,
   *  build, and collision all use it. Only the painted player avatar +
   *  camera position is shifted back by a tick. At 30 Hz that's
   *  barely perceptible; the smoothness payoff is huge. */
  getRenderPosition(nowMs: number, alpha: number): Vec2 {
    // Decay catch-up offset against wall-clock dt.
    if (this.lastRenderTime !== null) {
      const dtMs = nowMs - this.lastRenderTime;
      const k = Math.exp(-dtMs / CORRECTION_TIME_CONSTANT_MS);
      this.renderOffset.x *= k;
      this.renderOffset.y *= k;
      if (Math.abs(this.renderOffset.x) < OFFSET_SNAP_EPSILON) {
        this.renderOffset.x = 0;
      }
      if (Math.abs(this.renderOffset.y) < OFFSET_SNAP_EPSILON) {
        this.renderOffset.y = 0;
      }
    }
    this.lastRenderTime = nowMs;

    // Sub-frame lerp factor. `alpha` is the render loop's fixed-timestep
    // accumulator fraction — how far we are into the current 33.333 ms
    // sim step, in [0, 1). It is computed from the SAME `performance.now()`
    // clock that both steps the sim and paints the frame, so the lerp
    // advances perfectly uniformly with no timer-vs-vsync beat (the old
    // setTimeout-anchored factor froze for a few ms then hopped every
    // tick — the micro-jitter). Clamp defensively in case the caller
    // passes an out-of-range value.
    let t = alpha;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;

    return {
      x: this.predictedPrev.x + (this.predicted.x - this.predictedPrev.x) * t
        + this.renderOffset.x,
      y: this.predictedPrev.y + (this.predicted.y - this.predictedPrev.y) * t
        + this.renderOffset.y,
    };
  }

  currentDriftMagnitude(): number {
    const dx = this.renderOffset.x;
    const dy = this.renderOffset.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  debugInfo(): {
    historyLen: number;
    lastAck: number;
    serverX: number;
    serverY: number;
    drift: number;
    velX: number;
    velY: number;
    corrections: number;
    deadzoneHits: number;
  } {
    return {
      historyLen: this.history.size,
      lastAck: this.lastReconcileInputTick,
      serverX: this.lastReconcileServerPos.x,
      serverY: this.lastReconcileServerPos.y,
      drift: this.currentDriftMagnitude(),
      velX: this.velocity.x,
      velY: this.velocity.y,
      corrections: this.corrections,
      deadzoneHits: this.deadzoneHits,
    };
  }

  /** Apply one local input. Steps velocity toward the input's target,
   *  then advances position by the velocity through a swept-AABB
   *  collision against the current wall set. Stashes post-step velocity
   *  in the history entry so reconcile can restore it later. Called
   *  from main.ts BEFORE posting the input to the worker. Called from
   *  the render loop's fixed-timestep accumulator (see main.ts
   *  `frameHook`), so one call corresponds to exactly one sim step. */
  recordInput(sample: {
    tick: number;
    moveX: number;
    moveY: number;
  }): void {
    // Snapshot the position BEFORE this tick advances `predicted`.
    // The renderer lerps from this snapshot toward `predicted` over
    // the next sim step, smoothing the 30 Hz simulation into the
    // monitor's refresh rate (the lerp factor is the accumulator
    // fraction passed to `getRenderPosition`).
    this.predictedPrev.x = this.predicted.x;
    this.predictedPrev.y = this.predicted.y;

    const target = targetVelocity(sample);
    this.velocity.x = stepVelocityAxis(this.velocity.x, target.x);
    this.velocity.y = stepVelocityAxis(this.velocity.y, target.y);
    // Sweep velocity against walls; clamp position to the swept result.
    // Velocity itself stays unchanged (matches `apply_movement`: the
    // server only writes `pos.0` from the sweep, never `vel.0`). That
    // means holding D into a wall keeps `velocity.x` at MAX_SPEED but
    // position is pinned — release D and you accelerate away cleanly.
    // Circle-based collision — mirrors server's `sweep_circle`. AABB
    // sweep used to catch on cell corners with diagonal motion even
    // though the visible (circular) player would clear them.
    const swept = sweepCircle(
      this.predicted.x,
      this.predicted.y,
      PLAYER_HALF,
      this.velocity.x,
      this.velocity.y,
      this.wallLookup,
    );
    this.predicted.x = swept.finalX;
    this.predicted.y = swept.finalY;
    this.history.set(sample.tick, {
      tick: sample.tick,
      moveX: sample.moveX,
      moveY: sample.moveY,
      velocityAfterX: this.velocity.x,
      velocityAfterY: this.velocity.y,
    });
  }

  /** Reconcile against an authoritative server position. Snap-and-
   *  replay: position jumps to server's value at `lastInputTick`,
   *  velocity is restored from the history entry at that tick (so the
   *  upcoming replay starts from the right physics state), then all
   *  unacked inputs are stepped through to advance position back to
   *  "now". */
  reconcile(serverPos: Vec2, lastInputTick: number): void {
    this.lastReconcileServerPos.x = serverPos.x;
    this.lastReconcileServerPos.y = serverPos.y;
    this.lastReconcileInputTick = lastInputTick;

    // Deadzone: if the server agrees with our prediction to within a
    // sub-cell tolerance, DON'T correct. Keep the smooth locally-
    // integrated predicted position and velocity; just retire acked
    // inputs so the replay queue can't grow without bound. This is what
    // kills the permanent sub-pixel camera wobble — see
    // RECONCILE_DEADZONE_CELLS. Real disagreements fall through to the
    // snap-and-replay path below.
    const driftX = serverPos.x - this.predicted.x;
    const driftY = serverPos.y - this.predicted.y;
    if (
      driftX * driftX + driftY * driftY
      < RECONCILE_DEADZONE_CELLS * RECONCILE_DEADZONE_CELLS
    ) {
      this.deadzoneHits++;
      for (const t of this.history.keys()) {
        if (t <= lastInputTick) this.history.delete(t);
      }
      return;
    }
    this.corrections++;

    // Snap predicted to server's view; remember pre-snap predicted so
    // we can fold the visual delta into renderOffset.
    const oldPredictedX = this.predicted.x;
    const oldPredictedY = this.predicted.y;
    this.predicted.x = serverPos.x;
    this.predicted.y = serverPos.y;

    // Restore velocity to "right after lastInputTick was applied". If
    // we still have that history entry, take its stashed velocity; if
    // it's been trimmed (very stale ack), leave current velocity — the
    // input history kept it continuously updated, so it's close.
    const acked = this.history.get(lastInputTick);
    if (acked !== undefined) {
      this.velocity.x = acked.velocityAfterX;
      this.velocity.y = acked.velocityAfterY;
    }

    // Split history: ≤ack = drop, >ack = replay.
    const toReplay: InputSample[] = [];
    const toDrop: number[] = [];
    for (const [t, input] of this.history) {
      if (t <= lastInputTick) toDrop.push(t);
      else toReplay.push(input);
    }
    for (const t of toDrop) this.history.delete(t);
    toReplay.sort((a, b) => a.tick - b.tick);

    // Replay each unacked input: same step_velocity → sweep → position
    // chain as `recordInput`, refreshing the stashed velocityAfter as
    // we go so future reconciles see corrected values. All replays
    // use the current wall set — we don't track historical wall states.
    // That's an approximation (a wall placed/destroyed between the
    // server's tick T and "now" can shift collision results) but any
    // resulting drift is absorbed by `renderOffset` on the next
    // reconcile cycle.
    for (const i of toReplay) {
      const target = targetVelocity(i);
      this.velocity.x = stepVelocityAxis(this.velocity.x, target.x);
      this.velocity.y = stepVelocityAxis(this.velocity.y, target.y);
      const swept = sweepCircle(
        this.predicted.x,
        this.predicted.y,
        PLAYER_HALF,
        this.velocity.x,
        this.velocity.y,
        this.wallLookup,
      );
      this.predicted.x = swept.finalX;
      this.predicted.y = swept.finalY;
      i.velocityAfterX = this.velocity.x;
      i.velocityAfterY = this.velocity.y;
    }

    // Catch-up offset: keep rendered position UNCHANGED across the
    // reconcile, then decay back to zero. Hard-snap if the disagreement
    // is huge (respawn, teleport).
    const deltaX = oldPredictedX - this.predicted.x;
    const deltaY = oldPredictedY - this.predicted.y;
    const magnitudeSq = deltaX * deltaX + deltaY * deltaY;
    if (magnitudeSq > SNAP_THRESHOLD_CELLS * SNAP_THRESHOLD_CELLS) {
      this.renderOffset.x = 0;
      this.renderOffset.y = 0;
    } else {
      this.renderOffset.x += deltaX;
      this.renderOffset.y += deltaY;
    }

    // The sub-frame interpolator uses `predictedPrev → predicted` as
    // its endpoints. Reconcile just rewrote `predicted` to the
    // server's value (plus the replayed unacked inputs); the OLD
    // `predictedPrev` is now meaningless — it points at where we were
    // a tick ago in pre-reconcile coords. Snap it to `predicted` so
    // the lerp factor effectively re-anchors and we don't paint a
    // teleport from the stale prev to the new predicted. The visual
    // continuity is preserved by `renderOffset` (above), which already
    // absorbs the difference between where we WERE rendering and where
    // we're rendering now.
    this.predictedPrev.x = this.predicted.x;
    this.predictedPrev.y = this.predicted.y;
  }

  /** Wipe state. Used by main.ts on disconnect. */
  reset(): void {
    this.history.clear();
    this.predicted.x = 0;
    this.predicted.y = 0;
    this.predictedPrev.x = 0;
    this.predictedPrev.y = 0;
    this.velocity.x = 0;
    this.velocity.y = 0;
    this.renderOffset.x = 0;
    this.renderOffset.y = 0;
    this.lastRenderTime = null;
    this.lastReconcileServerPos.x = 0;
    this.lastReconcileServerPos.y = 0;
    this.lastReconcileInputTick = 0;
    this.corrections = 0;
    this.deadzoneHits = 0;
  }
}
